import { PlanTrailActionContext } from "./types";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import { knownMarkerElevation } from "../../../../../utils/elevation/repairElevations";
import { anchorToMarkers, mapMatchCoordinates, matchProfileFor } from "../../../../../utils/models/mapMatch";
import { IndexedLineString, ReplaceLineStringsCommand } from "../../undo/commands/ReplaceLineStringsCommand";

export interface MapMatchSummary {
  matched: number;
  failed: number;
  /** Furthest the drawn line strays from its match, across every matched segment. */
  maxDeviationMeters: number | null;
  errors: string[];
}

export const markerCoordinates = (marker: any): number[] | null => {
  const coords = marker?.markerGeometry?.coordinates;
  if (!coords || coords.length < 2) return null;
  // No placeholder 0 when the elevation is unknown: the bridge is left without a z and
  // validateAndCleanAllRoutes fills it from the matched line it joins.
  const ele = knownMarkerElevation(marker);
  return ele === undefined ? [coords[0], coords[1]] : [coords[0], coords[1], ele];
};

export const mapMatchActions = {
  /**
   * Admin tool: replace every line string of the active route with GraphHopper's map-match of
   * it, so an imported route gets turn instructions while keeping the line its author drew.
   *
   * Everything that matched lands as one undo step, so a result that looks wrong is one tap
   * to take back. A segment that fails to match is left exactly as it was.
   */
  async mapMatchActiveRoute({ state, dispatch }: PlanTrailActionContext): Promise<MapMatchSummary> {
    const summary: MapMatchSummary = { matched: 0, failed: 0, maxDeviationMeters: null, errors: [] };
    const routeKey = state.activeRouteKey;
    const route = routeKey ? state.routes[routeKey] : null;
    if (!routeKey || !route) return summary;

    const originals: IndexedLineString[] = [];
    const replacements: IndexedLineString[] = [];

    for (let index = 0; index < route.lineStrings.length; index++) {
      const original = route.lineStrings[index];
      const coordinates = original?.lineStringGeometry?.coordinates || [];
      if (coordinates.length < 2) continue;

      const start = markerCoordinates(route.markers[index]) || coordinates[0];
      const end = markerCoordinates(route.markers[index + 1]) || coordinates[coordinates.length - 1];

      try {
        const profile = original.lineStringProperties?.profile ?? null;
        const { graphooper, maxDeviationMeters } = await mapMatchCoordinates(coordinates, profile);
        const matched = anchorToMarkers(
          LineStringFeature.fromGraphooper(graphooper, matchProfileFor(profile)),
          start,
          end,
        );

        originals.push({ index, lineString: original });
        replacements.push({ index, lineString: matched });
        summary.matched += 1;
        summary.maxDeviationMeters = Math.max(summary.maxDeviationMeters ?? 0, maxDeviationMeters);
      } catch (error: any) {
        summary.failed += 1;
        summary.errors.push(error?.message || String(error));
      }
    }

    // The requests take a while; if the route was edited meanwhile, only swap segments that
    // are still the ones we matched, so a newer edit is never overwritten.
    const current = state.routes[routeKey];
    const stillCurrent = replacements.filter(
      ({ index }, i) => current?.lineStrings[index] === originals[i].lineString,
    );
    if (!current || stillCurrent.length === 0) return summary;
    const stillOriginal = originals.filter(({ index }) => stillCurrent.some((r) => r.index === index));

    state.undoManager.push(
      new ReplaceLineStringsCommand({
        type: 'MAP_MATCH_ROUTE',
        routeKey,
        oldLineStrings: stillOriginal,
        newLineStrings: stillCurrent,
      }),
    );
    for (const { index, lineString } of stillCurrent) {
      current.lineStrings[index] = lineString;
    }
    current.updated_at = new Date();

    summary.failed += replacements.length - stillCurrent.length;
    summary.matched = stillCurrent.length;
    await dispatch('updateRouteAndMarkers');
    return summary;
  },
};
